/**
 * 1D Navier-Stokes Network Flow Solver — Type Definitions
 *
 * Conservation equations (per cell, per time step):
 *   Mass:     ρ·V/dt + Σ(ṁ_f) = 0
 *   Momentum: ρ_f·A·(u*-u^n)/dt = -(P_R-P_L)·A/dx - f·ρ·u|u|·A/(2D)·dx - ρ·g·sin(θ)·A·dx
 *   Energy:   ρ·V·(h-h^n)/dt + Σ(ṁ_f·h_f) - V·(P-P^n)/dt = q_wall·A_wall
 *
 * Primary variables: P (Pa), h (J/kg) at cell centers; ṁ (kg/s) at faces.
 * All internal units are SI: Pa, J/kg, kg/m³, m/s, K, Pa·s
 */

export enum Phase {
  Subcooled = 'subcooled',
  TwoPhase = 'two-phase',
  Superheated = 'superheated',
  Supercritical = 'supercritical',
}

/** Extended fluid properties in SI units for the NS solver. */
export interface NSFluidProps {
  P: number;       // Pa
  T: number;       // K
  h: number;       // J/kg (specific enthalpy)
  s: number;       // J/(kg·K) (specific entropy)
  rho: number;     // kg/m³ (density)
  u_int: number;   // J/kg (specific internal energy)
  c: number;       // m/s (speed of sound)
  mu: number;      // Pa·s (dynamic viscosity)
  cp: number;      // J/(kg·K)
  cv: number;      // J/(kg·K)
  x: number;       // vapour quality (0-1 two-phase, <0 subcooled, >1 superheated)
  phase: Phase;
}

/** Cell state — stored at cell centers on the staggered grid. */
export interface CellState {
  P: number;       // Pa
  h: number;       // J/kg
  rho: number;     // kg/m³
  T: number;       // K
  u_int: number;   // J/kg
  x: number;       // vapour quality
  c: number;       // m/s (speed of sound)
  mu: number;      // Pa·s
  cp: number;      // J/(kg·K)
  phase: Phase;
}

/** Face state — stored at cell faces (staggered grid). */
export interface FaceState {
  mdot: number;    // kg/s — mass flow rate through face
  u: number;       // m/s — velocity (derived)
}

/** Pipe segment — discretized into N cells with staggered grid. */
export interface PipeSegment {
  id: string;
  diameter: number;       // m
  length: number;         // m
  roughness: number;      // m (absolute roughness)
  inclination: number;    // rad (angle from horizontal, positive = uphill)
  wallHeatFlux: number;   // W/m² (external heat transfer, positive = heat in)
  nCells: number;
  dx: number;             // m — cell length
  area: number;           // m² — cross-sectional area (π/4 × D²)

  // Optional: pipe wall thermal mass for transient thermal inertia
  wallThickness?: number;       // m — pipe wall thickness (0 = no wall thermal mass)
  wallRhoCp?: number;           // J/(m³·K) — wall material ρ×cp (steel: ~3.5e6)

  cells: CellState[];     // [nCells]
  faces: FaceState[];     // [nCells + 1]

  // Previous time step (for temporal discretization)
  cells_old: CellState[];
  faces_old: FaceState[];

  // Wall temperature tracking (optional, for thermal inertia)
  T_wall?: number[];       // [nCells] — wall temperature [K] per cell
  T_wall_old?: number[];   // [nCells] — previous timestep

  /**
   * Transient per-step diagnostic (N6): true when the homogeneous two-phase
   * friction multiplier hit its 50× stability cap anywhere in this pipe during
   * the latest momentum predictor pass. NOT serialized (recomputed every step);
   * never read by the solve itself — surfaced to simState.warnings so the
   * low-pressure wet-line dP under-prediction is visible.
   */
  twoPhaseFrictionCapEngaged?: boolean;
}

/** Junction — connects pipe faces to components. */
export interface Junction {
  id: string;
  type: 'component' | 'merge' | 'split' | 'boundary';
  connections: JunctionConnection[];
  P: number;       // Pa — junction pressure
  h: number;       // J/kg — junction enthalpy
  componentId?: string;
}

export interface JunctionConnection {
  pipeId: string;
  faceIndex: number;      // 0 = inlet face, nCells = outlet face
  direction: 'inflow' | 'outflow';
}

/**
 * Component boundary condition update.
 *
 * ENERGY-CHANNEL CONTRACT (Fix Wave W1 / N1):
 *   `deltaH` is the per-kg enthalpy change applied directly to the outlet
 *   boundary cell (h_out = h_junction + deltaH).
 *
 *   `energySource` (W) is a TOTAL heat rate into the throughflow that is NOT
 *   already carried by `deltaH`. The network solver (nsNetwork Step 4)
 *   converts it to a per-kg enthalpy change at apply time:
 *       Δh_Q = energySource / ṁ_outflow
 *   for HEAT-SOURCE component types only (boiler / heatExchanger / custom —
 *   the Q_rate-mode components). For work machines (pump / compressor /
 *   turbine) the energy transfer is already expressed through `deltaH`, so
 *   their `energySource` values are informational (shaft-work telemetry) and
 *   are deliberately NOT consumed — consuming them would double-count work.
 *
 *   `momentumSource` remains unconsumed (no per-face force injection path);
 *   hydraulic components express their effect through `deltaP` instead.
 */
export interface ComponentUpdate {
  deltaP: number;          // Pa — pressure jump/drop
  deltaH: number;          // J/kg — enthalpy change
  mdot?: number;           // kg/s — mass flow constraint (undefined = free)
  momentumSource: number;  // N — force on fluid (informational; not consumed)
  energySource: number;    // W — heat rate into fluid (consumed for heat-source types; see contract above)
  metadata: Record<string, number>;
}

/** NS component interface — each component type implements this. */
export interface NSComponent {
  id: string;
  type: string;
  params: Record<string, any>;
  applyBoundaryConditions(
    inletJunction: Junction,
    outletJunction: Junction,
    dt: number,
  ): ComponentUpdate;
}

/** SIMPLE solver configuration. */
export interface SIMPLEConfig {
  maxOuterIterations: number;     // max SIMPLE iterations per time step (default 20)
  convergenceTolerance: number;   // residual tolerance (default 1e-4)
  pressureRelaxation: number;     // α_P (default 0.3)
  velocityRelaxation: number;     // α_u (default 0.7)
  enthalpyRelaxation: number;     // α_h (default 0.8)
  enableAcoustic: boolean;        // enable MOC acoustic sub-step (default false)
}

/** Full solver state — persisted across frames. */
export interface NSSolverState {
  pipes: Map<string, PipeSegment>;
  junctions: Map<string, Junction>;
  components: Map<string, NSComponent>;
  config: SIMPLEConfig;
  time: number;            // s — current simulation time
  iteration: number;       // total SIMPLE iterations executed
  converged: boolean;
  residuals: {
    mass: number;
    momentum: number;
    energy: number;
  };
  energyBalance?: {
    totalInternalEnergy: number;  // J — sum of ρ·u_int·V across all cells
    totalKineticEnergy: number;   // J — sum of 0.5·ρ·u²·V across all cells
    imbalance: number;            // fractional imbalance vs previous step
  };
  /**
   * Transient per-step diagnostic (N6): true when ANY pipe engaged the 50×
   * two-phase friction cap during the latest networkSimpleStep. Aggregated
   * from PipeSegment.twoPhaseFrictionCapEngaged; not serialized; read-only
   * for warning surfacing (stepAdapter / solverWorker).
   */
  twoPhaseFrictionCapEngaged?: boolean;
}

/** Helper: create a default cell state. */
export function createCellState(P: number = 101325, h: number = 104930): CellState {
  return {
    P, h,
    rho: 998, T: 298.15, u_int: 104700, x: -1,
    c: 1497, mu: 0.001, cp: 4182, phase: Phase.Subcooled,
  };
}

/** Helper: create a default face state. */
export function createFaceState(mdot: number = 0): FaceState {
  return { mdot, u: 0 };
}

/** Default SIMPLE configuration. */
export const DEFAULT_SIMPLE_CONFIG: SIMPLEConfig = {
  maxOuterIterations: 50,
  convergenceTolerance: 1e-4,
  pressureRelaxation: 0.3,
  velocityRelaxation: 0.7,
  enthalpyRelaxation: 0.8,
  enableAcoustic: false,
};
